// Shared modal shell. Owns the chrome and mechanics that every modal in the
// app was duplicating: the fixed backdrop, centered panel, click-outside and
// Escape-to-close, focus management (focus on open, restore on close), and an
// optional standard header with a close (X) button.
//
// Two visual variants preserve the two looks already in the codebase:
//   - 'dialog' — compact (carbon/40 overlay, rounded-xl, p-6 panel, bottom
//     sheet on mobile, backdrop click closes). Used for confirmations and the
//     resource editor.
//   - 'sheet'  — taller scrollable form sheet (carbon/55 + blur overlay,
//     rounded-2xl, sticky bordered header, round close button). Backdrop click
//     does NOT close (matches the form modals, which guard against losing a
//     half-filled form to a stray click). Used for the event / partner editors
//     and the survey questionnaire. `fullScreenOnPhone` makes it fill a
//     phone's screen instead of floating, as the business card does.
//
// Callers render their own body (and footer) as children. When `title` is set,
// Modal renders the variant's header; otherwise the caller owns the header
// (e.g. the confirm dialog, which needs an icon + message).

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

type ModalVariant = 'dialog' | 'sheet';
type ModalSize = 'sm' | 'md' | 'lg' | 'form';

interface ModalProps {
  onClose: () => void;
  variant?: ModalVariant;
  size?: ModalSize;
  /** 'alertdialog' for destructive confirms; 'dialog' otherwise. */
  role?: 'dialog' | 'alertdialog';
  /** Standard header title. When set, Modal renders the variant's header
   *  (title + close button) and wires aria-labelledby automatically. Omit to
   *  render a fully custom header inside `children`. */
  title?: ReactNode;
  /** id of the caller's own heading, used for aria-labelledby when `title`
   *  is not supplied (custom-header callers). */
  labelledBy?: string;
  /** Element to focus when the modal opens. Without one, focus is left to a
   *  child's autoFocus. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Sheet only. By default the whole panel scrolls, which is right for a
   * plain form. Pass false when the caller pins its own header/footer and
   * scrolls just the middle: the panel becomes a flex column and stops
   * scrolling itself, so those pinned rows stay put.
   */
  scrollPanel?: boolean;
  /**
   * Sheet only. Below `md` the sheet fills the screen under the status bar,
   * edge to edge, instead of floating; from `md` up it is the ordinary
   * centred sheet. For a surface that is the whole task while it is open —
   * the business card, which is held up for someone to scan (IMP-008).
   */
  fullScreenOnPhone?: boolean;
  /**
   * Whether a click on the backdrop closes the modal. Defaults to the
   * variant's rule: a dialog closes, a sheet doesn't (a stray click must not
   * cost a half-filled form). A sheet with nothing to lose can opt in.
   */
  closeOnBackdrop?: boolean;
  children: ReactNode;
}

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  // A full form in a sheet. The fixed sizes above are the same on a phone and
  // on a 27-inch monitor, which left the Edit Purchase Request form with ~470px
  // of usable width at every size; this one grows with the viewport and stops
  // where a form stops getting easier to read.
  form: 'max-w-lg lg:max-w-5xl 2xl:max-w-[1440px]',
};

export function Modal({
  onClose,
  variant = 'dialog',
  size = 'md',
  role = 'dialog',
  title,
  labelledBy,
  initialFocusRef,
  scrollPanel = true,
  fullScreenOnPhone = false,
  closeOnBackdrop,
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // The element focused before the modal opened, restored on close so keyboard
  // users aren't dropped to <body>.
  const restoreRef = useRef<HTMLElement | null>(null);
  const generatedTitleId = useId();
  const titleId = title != null ? generatedTitleId : labelledBy;

  // The latest onClose, for the Escape key. Read through a ref so the effect
  // below runs once per open: with onClose among its dependencies, a parent
  // that passes an inline function re-ran it on every render, and each re-run
  // sent focus back to whatever was focused before the modal opened — in the
  // survey questionnaire, every answer you picked.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    // Move focus to an explicit target when given (e.g. the confirm button).
    // Otherwise leave it to a child's autoFocus — the form modals autofocus
    // their first field, and stealing focus to the panel would undo that.
    initialFocusRef?.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      restoreRef.current?.focus?.();
    };
  }, [initialFocusRef]);

  const isSheet = variant === 'sheet';
  const phoneFullScreen = isSheet && fullScreenOnPhone;
  // 'sheet' guards against closing a half-filled form on a stray backdrop
  // click; 'dialog' closes (cancel) on backdrop click.
  const backdropCloses = closeOnBackdrop ?? !isSheet;

  // Top padding clears the status bar (pt-safe-4 = inset + 1rem) and the
  // sheet is capped at the space that leaves, so a tall form never starts
  // under the status bar on an iPhone, where it could not be tapped. A
  // full-screen sheet clears just the inset on a phone and keeps the usual
  // 1rem margin from md up, where there is no status bar to clear.
  const backdropClass = phoneFullScreen
    ? 'fixed inset-0 z-50 flex items-center justify-center bg-carbon/55 pt-safe backdrop-blur-sm animate-fade-in md:p-4'
    : isSheet
      ? 'fixed inset-0 z-50 flex items-center justify-center bg-carbon/55 px-4 pb-4 pt-safe-4 backdrop-blur-sm animate-fade-in'
      : 'fixed inset-0 z-50 flex items-end justify-center bg-carbon/40 px-4 pb-4 pt-safe-4 sm:items-center animate-fade-in';

  const scrollClass = scrollPanel ? 'overflow-y-auto' : 'flex flex-col overflow-hidden';
  // The full-screen panel cancels the size cap below md (max-md:max-w-none)
  // so it spans the phone's width; from md up it is the sheet below.
  const panelClass = phoneFullScreen
    ? `h-full w-full ${SIZE[size]} ${scrollClass} bg-white max-md:max-w-none md:h-auto md:max-h-[min(90vh,100%)] md:rounded-2xl md:shadow-e3`
    : isSheet
      ? `max-h-[min(90vh,100%)] w-full ${SIZE[size]} ${scrollClass} rounded-2xl bg-white shadow-e3`
      : `w-full ${SIZE[size]} rounded-xl bg-white p-6 shadow-e3`;

  return (
    <div
      className={backdropClass}
      role={role}
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={backdropCloses ? onClose : undefined}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`${panelClass} focus:outline-none`}
        onClick={(e) => e.stopPropagation()}
      >
        {title != null &&
          (isSheet ? (
            <div className="sticky top-0 flex items-center justify-between border-b border-cloud bg-white p-4">
              <h2 id={titleId} className="text-lg font-bold text-carbon">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-cloud bg-white text-steel transition hover:bg-cloud/30 active:scale-90"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="mb-4 flex items-center justify-between">
              <h2 id={titleId} className="text-lg font-medium text-processBlue">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-steel hover:bg-cloud/30"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          ))}
        {children}
      </div>
    </div>
  );
}
